import React, { useMemo, useState } from 'react';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
} from 'chart.js';
import {
    Box,
    Heading,
    SimpleGrid,
    Card,
    CardHeader,
    CardBody,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tag,
    useColorModeValue,
    Input,
    Select,
    Stack,
    Button,
    Text,
} from '@chakra-ui/react';
import { getPlanLabel, isPaidUser, PAID_TIER_LABEL } from '../config/adminConfig';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

function UserInfo({ users = [] }) {
    const [search, setSearch] = useState("");
    const [otpFilter, setOtpFilter] = useState("all");
    const [premiumFilter, setPremiumFilter] = useState("all");
    const [onboardingFilter, setOnboardingFilter] = useState("all");

    const filteredUsers = useMemo(() => {
        return users
            .filter(user => user.email !== 'info@finansee.eu')
            .filter(user => {
                const matchesSearch =
                    user.name?.toLowerCase().includes(search.toLowerCase()) ||
                    user.email?.toLowerCase().includes(search.toLowerCase());

                const matchesOtp =
                    otpFilter === "all" ||
                    (otpFilter === "verified" && user.isOtpVerified) ||
                    (otpFilter === "unverified" && !user.isOtpVerified);

                const matchesPremium =
                    premiumFilter === "all" ||
                    (premiumFilter === "free" && user.isPremium === 0) ||
                    (premiumFilter === "premium" && isPaidUser(user.isPremium));

                const matchesOnboarding =
                    onboardingFilter === "all" ||
                    (onboardingFilter === "complete" && user.onboardingCompleted) ||
                    (onboardingFilter === "incomplete" && !user.onboardingCompleted);

                return matchesSearch && matchesOtp && matchesPremium && matchesOnboarding;
            });
    }, [users, search, otpFilter, premiumFilter, onboardingFilter]);

    const insights = useMemo(() => {
        if (!filteredUsers.length) return {};

        const totalUsers = filteredUsers.length;
        const premiumUsers = filteredUsers.filter(user => isPaidUser(user.isPremium)).length;
        const completedOnboarding = filteredUsers.filter(user => user.onboardingCompleted).length;
        const verifiedUsers = filteredUsers.filter(user => user.isOtpVerified).length;

        const ageDistribution = {};
        const incomeDistribution = {};
        const educationDistribution = {};
        const employmentDistribution = {};
        const riskToleranceDistribution = {};

        filteredUsers.forEach(user => {
            if (user.onboardingAnswers?.length > 0) {
                user.onboardingAnswers.forEach(answer => {
                    if (answer.questionId === "1") ageDistribution[answer.answer] = (ageDistribution[answer.answer] || 0) + 1;
                    else if (answer.questionId === "5") incomeDistribution[answer.answer] = (incomeDistribution[answer.answer] || 0) + 1;
                    else if (answer.questionId === "3") educationDistribution[answer.answer] = (educationDistribution[answer.answer] || 0) + 1;
                    else if (answer.questionId === "4") employmentDistribution[answer.answer] = (employmentDistribution[answer.answer] || 0) + 1;
                    else if (answer.questionId === "13") riskToleranceDistribution[answer.answer] = (riskToleranceDistribution[answer.answer] || 0) + 1;
                });
            }
        });

        const registrationByMonth = {};
        filteredUsers.forEach(user => {
            const date = new Date(user.createdAt);
            const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            registrationByMonth[monthYear] = (registrationByMonth[monthYear] || 0) + 1;
        });

        return {
            totalUsers,
            premiumUsers,
            completedOnboarding,
            verifiedUsers,
            ageDistribution,
            incomeDistribution,
            educationDistribution,
            employmentDistribution,
            riskToleranceDistribution,
            registrationByMonth
        };
    }, [filteredUsers]);

    const percentage = (value, total) => {
        if (!total) return '0.0';
        return ((value / total) * 100).toFixed(1);
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
    };

    const premiumStatusData = {
        labels: [PAID_TIER_LABEL, 'Free Users'],
        datasets: [{
            data: [insights.premiumUsers, insights.totalUsers - insights.premiumUsers],
            backgroundColor: ['#10B981', '#6B7280'],
            borderWidth: 2,
            borderColor: '#fff'
        }]
    };

    const onboardingStatusData = {
        labels: ['Completed', 'Not Completed'],
        datasets: [{
            data: [insights.completedOnboarding, insights.totalUsers - insights.completedOnboarding],
            backgroundColor: ['#3B82F6', '#EF4444'],
            borderWidth: 2,
            borderColor: '#fff'
        }]
    };

    const ageDistributionData = {
        labels: Object.keys(insights.ageDistribution || {}),
        datasets: [{
            label: 'Users by Age Group',
            data: Object.values(insights.ageDistribution || {}),
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 1
        }]
    };

    const incomeDistributionData = {
        labels: Object.keys(insights.incomeDistribution || {}),
        datasets: [{
            label: 'Users by Income Level',
            data: Object.values(insights.incomeDistribution || {}),
            backgroundColor: 'rgba(16, 185, 129, 0.6)',
            borderColor: 'rgba(16, 185, 129, 1)',
            borderWidth: 1
        }]
    };

    const riskToleranceData = {
        labels: Object.keys(insights.riskToleranceDistribution || {}),
        datasets: [{
            data: Object.values(insights.riskToleranceDistribution || {}),
            backgroundColor: ['#EF4444', '#F59E0B', '#10B981', '#8B5CF6'],
            borderWidth: 2,
            borderColor: '#fff'
        }]
    };

    const registrationTimelineData = {
        labels: Object.keys(insights.registrationByMonth || {}).sort(),
        datasets: [{
            label: 'New Registrations',
            data: Object.keys(insights.registrationByMonth || {}).sort().map(month => insights.registrationByMonth[month]),
            borderColor: 'rgba(139, 92, 246, 1)',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            fill: true,
            tension: 0.4
        }]
    };

    const cardBg = useColorModeValue('white', 'gray.800');

    // CSV Export Function
    const exportToCSV = () => {
        const headers = ["Name", "Email", "Otp Verified", "Premium Status", "Registered", "Onboarding"];
        const rows = filteredUsers.map(user => [
            user.name || "",
            user.email || "",
            user.isOtpVerified ? "Verified" : "Unverified",
            getPlanLabel(user.isPremium),
            new Date(user.createdAt).toLocaleDateString(),
            user.onboardingCompleted ? "Complete" : "Incomplete"
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(val => `"${val}"`).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "users.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')} p={6}>
            <Box maxW="7xl" mx="auto">
                <Heading size="xl" mb={8}>User Analytics Dashboard</Heading>

                {/* Key Metrics */}
                <SimpleGrid columns={[1, 2, 4]} spacing={6} mb={8}>
                    <StatCard label="Total Users" value={insights.totalUsers} color="gray.900" />
                    <StatCard
                        label={PAID_TIER_LABEL}
                        value={insights.premiumUsers}
                        color="green.500"
                        help={`${percentage(insights.premiumUsers, insights.totalUsers)}% conversion`}
                    />
                    <StatCard
                        label="Onboarding Complete"
                        value={insights.completedOnboarding}
                        color="blue.500"
                        help={`${percentage(insights.completedOnboarding, insights.totalUsers)}% completion`}
                    />
                    <StatCard
                        label="Verified Users"
                        value={insights.verifiedUsers}
                        color="purple.500"
                        help={`${percentage(insights.verifiedUsers, insights.totalUsers)}% verified`}
                    />
                </SimpleGrid>

                <Card bg={cardBg} shadow="sm" mb={8}>
                    <CardBody>
                        <Text color="gray.600">
                            This dashboard is aligned to the current B2C operating model: <strong>Free + Premium</strong>.
                            All paid users are now tracked in one premium bucket so onboarding, verification and conversion stay comparable.
                        </Text>
                    </CardBody>
                </Card>

                {/* Charts */}
                <SimpleGrid columns={[1, 2]} spacing={6} mb={8}>
                    <ChartCard title="Premium vs Free Users"><Doughnut data={premiumStatusData} options={chartOptions} /></ChartCard>
                    <ChartCard title="Onboarding Completion"><Doughnut data={onboardingStatusData} options={chartOptions} /></ChartCard>
                    <ChartCard title="Age Distribution"><Bar data={ageDistributionData} options={{ ...chartOptions, indexAxis: 'y' }} /></ChartCard>
                    <ChartCard title="Risk Tolerance Profile"><Pie data={riskToleranceData} options={chartOptions} /></ChartCard>
                </SimpleGrid>

                <ChartCard title="User Registration Timeline" mb={8}>
                    <Line data={registrationTimelineData} options={chartOptions} />
                </ChartCard>

                <ChartCard title="Income Distribution" mb={8}>
                    <Bar data={incomeDistributionData} options={chartOptions} />
                </ChartCard>

                {/* User Directory */}
                <Card bg={cardBg} shadow="md">
                    <CardHeader display="flex" justifyContent="space-between" alignItems="center">
                        <Heading size="md">User Directory</Heading>
                        <Button colorScheme="blue" size="sm" onClick={exportToCSV}>
                            Export CSV
                        </Button>
                    </CardHeader>
                    <CardBody overflowX="auto">
                        {/* Search + Filters */}
                        <Stack direction={['column', 'row']} spacing={4} mb={4}>
                            <Input
                                placeholder="Search by name or email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <Select value={otpFilter} onChange={(e) => setOtpFilter(e.target.value)}>
                                <option value="all">All OTP Status</option>
                                <option value="verified">Verified</option>
                                <option value="unverified">Unverified</option>
                            </Select>
                            <Select value={premiumFilter} onChange={(e) => setPremiumFilter(e.target.value)}>
                                <option value="all">All Plans</option>
                                <option value="free">Free</option>
                                <option value="premium">Premium</option>
                            </Select>
                            <Select value={onboardingFilter} onChange={(e) => setOnboardingFilter(e.target.value)}>
                                <option value="all">All Onboarding</option>
                                <option value="complete">Complete</option>
                                <option value="incomplete">Incomplete</option>
                            </Select>
                        </Stack>

                        <Table variant="striped" colorScheme="gray">
                            <Thead>
                                <Tr>
                                    <Th>Name</Th>
                                    <Th>Email</Th>
                                    <Th>Otp Verified Status</Th>
                                    <Th>Premium Status</Th>
                                    <Th>Registered</Th>
                                    <Th>Onboarding</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {filteredUsers.map(user => (
                                    <Tr key={user._id}>
                                        <Td>{user.name}</Td>
                                        <Td>{user.email}</Td>
                                        <Td>
                                            <Tag colorScheme={user.isOtpVerified ? "green" : "red"}>
                                                {user.isOtpVerified ? "Verified" : "Unverified"}
                                            </Tag>
                                        </Td>
                                        <Td>
                                            <Tag
                                                colorScheme={
                                                    user.isPremium >= 1 ? "blue" : "gray"
                                                }
                                            >
                                                {getPlanLabel(user.isPremium)}
                                            </Tag>
                                        </Td>
                                        <Td>{new Date(user.createdAt).toLocaleDateString()}</Td>
                                        <Td>
                                            <Tag colorScheme={user.onboardingCompleted ? "green" : "yellow"}>
                                                {user.onboardingCompleted ? "Complete" : "Incomplete"}
                                            </Tag>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </CardBody>
                </Card>
            </Box>
        </Box>
    );
}

function StatCard({ label, value, color, help }) {
    return (
        <Card shadow="md">
            <CardBody>
                <Stat>
                    <StatLabel>{label}</StatLabel>
                    <StatNumber color={color}>{value}</StatNumber>
                    {help && <StatHelpText>{help}</StatHelpText>}
                </Stat>
            </CardBody>
        </Card>
    );
}

function ChartCard({ title, children, mb }) {
    const cardBg = useColorModeValue('white', 'gray.800');
    return (
        <Card bg={cardBg} shadow="md" mb={mb}>
            <CardHeader>
                <Heading size="md">{title}</Heading>
            </CardHeader>
            <CardBody h="300px">{children}</CardBody>
        </Card>
    );
}

export default UserInfo;
